import React from 'react';
import { Wrench } from 'lucide-react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { getToolUseDisplay } from '../../utils/toolUse';
import { getSourceIcon } from '../../utils/sourceIcons';
import { useLayoutStore } from '../../../../store/layoutStore';
import { ShimmerText, MorphingFluid } from '../ChatArea/Indicators';

interface ToolUseContentProps {
  content: ToolUseBlock;
  onExpand?: () => void;
  preventToolUseBlockToCollapse?: boolean;
  /** Override the outer margin class. Defaults to 'my-0.5'. Pass 'my-0' when rendering inside ToolUseGroup. */
  outerClassName?: string;
}

export const ToolUseContent: React.FC<ToolUseContentProps> = ({
  content,
  onExpand,
  preventToolUseBlockToCollapse = false,
  outerClassName = 'my-0.5',
}) => {
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );
  const setToolPanelOpen = useChatStore((state) => state.setToolPanelOpen);
  const pendingToolUses = useChatStore((state) =>
    activeConversationId
      ? state.conversations[activeConversationId]?.pendingToolUses ||
        new Set<string>()
      : new Set<string>(),
  );
  const setSelectedToolId = useChatStore((state) => state.setSelectedToolId);
  const selectedToolId = useChatStore((state) => state.selectedToolId);
  const isOpen = useChatStore((state) => {
    const activeConversation = state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : null;
    return activeConversation?.toolPanelOpen || false;
  });

  const isLoading = useChatStore(
    (state) => state.conversations[state.activeConversationId!]?.isLoading,
  );

  const isMiniMode = useLayoutStore((state) => state.isMiniMode);
  const setMiniMode = useLayoutStore((state) => state.setMiniMode);

  const isPending = isLoading && pendingToolUses.has(content.id);
  const isSelected =
    content.id === selectedToolId && isOpen && !preventToolUseBlockToCollapse;

  const source = (content.meta as { source?: string })?.source;
  const sourceIcon = source ? getSourceIcon(source) : null;

  const handleClick = () => {
    if (activeConversationId && !isPending) {
      if (isMiniMode) {
        setMiniMode(false);
      }
      onExpand?.();

      if (isSelected) {
        setToolPanelOpen(activeConversationId, false);
        setSelectedToolId(null);
      } else {
        setToolPanelOpen(activeConversationId, true);
        setSelectedToolId(content.id);
      }
    }
  };

  const displayName = getToolUseDisplay(content);

  // ── Pending ──────────────────────────────────────────────────────────────────
  if (isPending) {
    return (
      <div
        className={`${outerClassName} flex items-center gap-2.5 rounded-lg bg-orange-50/60 px-2.5 py-1.5 select-none dark:bg-gray-700/10`}
      >
        {/* h-5 w-5 container matches the done-state icon area — no layout jump on transition */}
        <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-[#FF732E]/10 dark:bg-[#FF732E]/15">
          <MorphingFluid size="h-3.5 w-3.5" />
        </div>
        <ShimmerText text={`${displayName}…`} />
      </div>
    );
  }

  // ── Done ─────────────────────────────────────────────────────────────────────
  return (
    <div
      data-tool-id={content.id}
      onClick={handleClick}
      className={`group/pill ${outerClassName} flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 transition-all duration-150 select-none ${
        isSelected
          ? 'bg-orange-50/40 dark:bg-orange-950/20'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'
      }`}
    >
      {/* Icon */}
      <div className="flex-shrink-0">
        {sourceIcon ? (
          <div className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-md border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-800">
            {sourceIcon}
          </div>
        ) : (
          <div className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-md border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-800">
            <Wrench className="h-3.5 w-3.5 text-[#FF732E]" strokeWidth={2} />
          </div>
        )}
      </div>

      {/* Label */}
      <span
        className={`min-w-0 flex-1 truncate text-sm font-normal tracking-wide transition-colors duration-150 ${
          isSelected
            ? 'text-gray-500 dark:text-gray-400'
            : 'text-gray-500 dark:text-gray-400'
        }`}
      >
        {displayName}
      </span>

      {/* Arrow — hover-only in both selected and unselected states */}
      <svg
        className={`h-3.5 w-3.5 flex-shrink-0 text-transparent transition-all duration-150 ${
          isSelected
            ? 'group-hover/pill:text-[#FF732E]'
            : 'group-hover/pill:text-gray-400 dark:group-hover/pill:text-gray-500'
        }`}
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="2.5"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
        />
      </svg>
    </div>
  );
};
